<template>
  <div class="container mt-4">
    <h4 class="mb-4">
      <i class="material-icons text-danger me-2">event_busy</i>
      Review Leave Requests
    </h4>

    <div class="card shadow border-0">
      <div class="card-header text-white fw-bold" style="background-color: #b03a45;">
        <i class="material-icons me-2">description</i> Pending / Recent Leave Requests
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="text-uppercase small" style="background-color: #f8f9fa;">
            <tr>
              <th>Employee</th>
              <th>Type</th>
              <th>Dates</th>
              <th>Days</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Certificate</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="leave in leaves" :key="leave.id">
              <td>
                <div class="fw-semibold">{{ leave.user?.firstname }} {{ leave.user?.lastname }}</div>
                <div class="text-muted small">{{ leave.user?.email || '' }}</div>
              </td>
              <td>{{ $t(`leaveRequests.${leave.type.toLowerCase()}`) || leave.type }}</td>
              <td>{{ leave.start_date }} → {{ leave.end_date }}</td>
              <td>{{ leave.days }}</td>
              <td>{{ leave.reason || '—' }}</td>
              <td>
                <span :class="['badge', statusClass(leave.status)]">
                  {{ leave.status }}
                </span>
              </td>
              <td>
                <a
                  v-if="leave.certificate_path"
                  :href="`http://127.0.0.1:8000/storage/${leave.certificate_path}`"
                  target="_blank"
                  class="btn btn-sm btn-outline-danger"
                >
                  VIEW
                </a>
                <span v-else>—</span>
              </td>
              <td>
                <button
                  v-if="leave.status === 'Pending'"
                  class="btn btn-success btn-sm me-1"
                  :disabled="actioningId === leave.id"
                  @click="approveLeave(leave.id)"
                >
                  ✔ Approve
                </button>
                <button
                  v-if="leave.status === 'Pending'"
                  class="btn btn-danger btn-sm"
                  :disabled="actioningId === leave.id"
                  @click="rejectLeave(leave.id)"
                >
                  ✘ Reject
                </button>
              </td>
            </tr>

            <tr v-if="leaves.length === 0">
              <td colspan="8" class="text-center text-muted py-4">
                No leave requests found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/api' // sinon '../api'
import { useToast } from 'vue-toastification'

const toast = useToast()
const leaves = ref([])
const actioningId = ref(null)

const fetchLeaves = async () => {
  try {
    const res = await axios.get('/employee/AllLeaves') // route confirmée par tes captures
    leaves.value = res.data.leaves || []
  } catch (err) {
    toast.error('❌ Failed to load leave requests')
    console.error(err)
  }
}

const approveLeave = async (id) => {
  if (!confirm('Approve this leave request?')) return
  actioningId.value = id
  try {
    await axios.patch(`/employee/leaves/approve/${id}`)
    toast.success('✅ Leave approved')
    await fetchLeaves()
  } catch (err) {
    toast.error('❌ Approval failed')
    console.error(err)
  } finally {
    actioningId.value = null
  }
}

const rejectLeave = async (id) => {
  if (!confirm('Reject this leave request?')) return
  actioningId.value = id
  try {
    await axios.patch(`/employee/leaves/reject/${id}`)
    toast.success('✅ Leave rejected')
    await fetchLeaves()
  } catch (err) {
    toast.error('❌ Rejection failed')
    console.error(err)
  } finally {
    actioningId.value = null
  }
}

const statusClass = (status) => {
  switch (status) {
    case 'Approved': return 'bg-success text-white'
    case 'Rejected': return 'bg-danger text-white'
    default: return 'bg-warning text-dark' // Pending
  }
}

onMounted(fetchLeaves)
</script>

<style scoped>
.badge {
  padding: 0.4em 0.8em;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 0.75rem;
}
</style>
